class BerriVideoReviews extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.videoSlides = this.querySelectorAll('[data-video-slide]');
    this.atcPanels = this.querySelectorAll('[data-atc-panel]');
    this.atcHost = this.querySelector('[data-atc-host]');
    this.reviewSlides = this.querySelectorAll('[data-review-slide]');
    this.prevButtons = this.querySelectorAll('[data-berri-vr-prev]');
    this.nextButtons = this.querySelectorAll('[data-berri-vr-next]');
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.resumeTimer = null;

    this.initVideoControls();

    if (this.videoSlides.length < 1) return;

    this.prevButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (this.currentIndex > 0) {
          this.goToSlide(this.currentIndex - 1);
          this.pauseAutoplay();
        }
      });
    });

    this.nextButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (this.currentIndex < this.videoSlides.length - 1) {
          this.goToSlide(this.currentIndex + 1);
          this.pauseAutoplay();
        }
      });
    });

    requestAnimationFrame(() => {
      this.goToSlide(0);
      if (this.autoplayEnabled && this.videoSlides.length > 1) this.playAutoplay();
    });

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.autoplayEnabled && this.videoSlides.length > 1) this.playAutoplay();
    });
  }

  disconnectedCallback() {
    this._initialized = false;
    this.stopAutoplay();
    window.clearTimeout(this.resumeTimer);
  }

  initVideoControls() {
    this.querySelectorAll('[data-video-slide]').forEach((slide) => {
      const container = slide.querySelector('.berri-video-reviews__video-container');
      const video = slide.querySelector('video');
      const playBtn = slide.querySelector('[data-video-play]');

      if (!video) return;

      const togglePlayback = (event) => {
        event?.preventDefault();
        event?.stopPropagation();

        playBtn?.classList.add('is-visible');

        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      };

      playBtn?.addEventListener('click', togglePlayback);

      container?.addEventListener('click', (event) => {
        if (event.target.closest('[data-video-play]')) return;
        togglePlayback(event);
      });

      video.addEventListener('play', () => this.syncPlayButton(video, playBtn));
      video.addEventListener('pause', () => this.syncPlayButton(video, playBtn));
    });
  }

  syncPlayButton(video, playBtn) {
    if (!playBtn) return;

    const isPlaying = !video.paused && !video.ended;
    playBtn.classList.toggle('is-playing', isPlaying);
    playBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    playBtn.setAttribute(
      'aria-label',
      isPlaying ? 'Pause video' : playBtn.dataset.playLabel || 'Play video'
    );
  }

  goToSlide(index) {
    this.currentIndex = Math.min(this.videoSlides.length - 1, Math.max(0, index));
    this.updateVideoSlides();
    this.updateAtcPanels();
    this.updateReviewSlides();
    this.updateVideos();
    this.updateButtons();
  }

  updateVideoSlides() {
    this.videoSlides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  updateAtcPanels() {
    if (!this.atcPanels.length) return;

    let hasVisibleProduct = false;

    this.atcPanels.forEach((panel, index) => {
      const isActive = index === this.currentIndex;
      const hasProduct = panel.querySelector('.berri-video-reviews__atc-container') !== null;

      panel.classList.toggle('is-active', isActive && hasProduct);
      panel.setAttribute('aria-hidden', isActive && hasProduct ? 'false' : 'true');

      if (isActive && hasProduct) hasVisibleProduct = true;
    });

    if (this.atcHost) {
      this.atcHost.classList.toggle('is-empty', !hasVisibleProduct);
    }
  }

  updateReviewSlides() {
    this.reviewSlides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  updateVideos() {
    this.videoSlides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      const playBtn = slide.querySelector('[data-video-play]');
      if (!video) return;

      if (index === this.currentIndex) {
        playBtn?.classList.remove('is-visible');
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
        playBtn?.classList.remove('is-visible');
      }

      this.syncPlayButton(video, playBtn);
    });
  }

  updateButtons() {
    const count = this.videoSlides.length;
    const atStart = this.currentIndex <= 0;
    const atEnd = this.currentIndex >= count - 1;
    const singleSlide = count <= 1;

    this.prevButtons.forEach((button) => {
      button.toggleAttribute('disabled', singleSlide || atStart);
    });

    this.nextButtons.forEach((button) => {
      button.toggleAttribute('disabled', singleSlide || atEnd);
    });
  }

  playAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || this.videoSlides.length < 2) return;

    this.autoplayTimer = window.setInterval(() => {
      if (this.currentIndex < this.videoSlides.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      } else {
        this.stopAutoplay();
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    window.clearInterval(this.autoplayTimer);
  }

  pauseAutoplay() {
    this.stopAutoplay();
    window.clearTimeout(this.resumeTimer);
    this.resumeTimer = window.setTimeout(() => {
      if (this.autoplayEnabled && this.videoSlides.length > 1) this.playAutoplay();
    }, 8000);
  }
}

customElements.define('berri-video-reviews', BerriVideoReviews);
